import { useState, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useInView } from "react-intersection-observer";
import {
    collection,
    query,
    orderBy,
    limit,
    startAfter,
    getDocs,
    deleteDoc,
    doc,
    addDoc,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase";
import Window from "../components/Window";
import Password from "../components/Password";
import Loading from "../components/Loading";

const Gallery = () => {
    const [passwordMode, setPasswordMode] = useState(null); // 'upload' or 'delete'
    const [showUpload, setShowUpload] = useState(false);
    const [images, setImages] = useState([]);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const pageSize = 12;

    const [deleteLoading, setDeleteLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);

    const [filterDrawer, setFilterDrawer] = useState("전체");
    const [filterSouta, setFilterSouta] = useState("전체");
    const [filterHoshi, setFilterHoshi] = useState("전체");

    const [selectedImage, setSelectedImage] = useState(null);
    const [ref, inView] = useInView();

    const [pendingDelete, setPendingDelete] = useState(null);

    const onClose = () => {
        setShowUpload(false);
        setImage(null);
        setImagePreview(null);
    };

    const openPasswordModal = (mode) => setPasswordMode(mode);
    const closePasswordModal = () => setPasswordMode(null);

    const handlePasswordSuccess = () => {
        if (passwordMode === "upload") {
            setShowUpload(true);
        } else if (passwordMode === "delete" && pendingDelete) {
            handleDelete(pendingDelete.id, pendingDelete.fileName);
        }
        setPasswordMode(null);
    };

    const fetchInitialImages = useCallback(async () => {
        setLoading(true);
        const q = query(collection(db, "gallery"), orderBy("createdAt", "desc"), limit(pageSize));
        const snapshot = await getDocs(q);
        if (snapshot.empty) {
            setHasMore(false);
            setImages([]);
            setLastVisible(null);
        } else {
            const newImages = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setImages(newImages);
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        setImages([]);
        setLastVisible(null);
        setHasMore(true);
        fetchInitialImages();
    }, [filterDrawer, filterSouta, filterHoshi, fetchInitialImages]);

    const loadMore = useCallback(async () => {
        if (!lastVisible || loading || !hasMore) return;
        setLoading(true);
        const nextQuery = query(
            collection(db, "gallery"),
            orderBy("createdAt", "desc"),
            startAfter(lastVisible),
            limit(pageSize)
        );
        const snapshot = await getDocs(nextQuery);
        if (!snapshot.empty) {
            const newImages = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setImages((prev) => [...prev, ...newImages]);
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        } else {
            setHasMore(false);
        }
        setLoading(false);
    }, [lastVisible, loading, hasMore]);

    useEffect(() => {
        if (inView && hasMore) loadMore();
    }, [inView, hasMore, loadMore]);

    const handleDelete = async (id, fileName) => {
        try {
            setDeleteLoading(true);
            await deleteDoc(doc(db, "gallery", id));
            const imageRef = storageRef(storage, `gallery/${fileName}`);
            await deleteObject(imageRef);

            setImages((prev) => prev.filter((img) => img.id !== id));
            setSelectedImage(null);
            setPendingDelete(null);
        } catch (err) {
            console.error("삭제 실패:", err);
            alert("삭제에 실패했어요 ㅠㅠ");
        } finally {
            setDeleteLoading(false);
        }
    };

    const filteredImages = images.filter((img) => {
        const matchesDrawer = filterDrawer === "전체" || img.drawer === filterDrawer;
        const matchesSouta = filterSouta === "전체" || img.souta === filterSouta;
        const matchesHoshi = filterHoshi === "전체" || img.hoshi === filterHoshi;
        return matchesDrawer && matchesSouta && matchesHoshi;
    });

    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [drawer, setDrawer] = useState("커미션");
    const [souta, setSouta] = useState("오리진소우타");
    const [hoshi, setHoshi] = useState("오리진호시");

    const onFileChange = (e) => {
        const file = e.target.files?.[0] || null;
        setImage(file);
        setImagePreview(file ? URL.createObjectURL(file) : null);
    };

    useEffect(() => {
        return () => {
            if (imagePreview) URL.revokeObjectURL(imagePreview);
        };
    }, [imagePreview]);

    const handleUpload = async () => {
        if (!image) return alert("이미지를 선택해주세요!");

        try {
            setUploadLoading(true);
            const uniqueFileName = `${uuidv4()}_${image.name}`;
            const imageRef = storageRef(storage, `gallery/${uniqueFileName}`);
            await uploadBytes(imageRef, image);
            const url = await getDownloadURL(imageRef);

            await addDoc(collection(db, "gallery"), {
                imageUrl: url,
                fileName: uniqueFileName,
                drawer,
                souta,
                hoshi,
                createdAt: new Date(),
            });

            onClose();
            fetchInitialImages();
        } catch (err) {
            console.error(err);
            alert("업로드 실패 ㅠㅠ");
        } finally {
            setUploadLoading(false);
        }
    };

    const filters = [
        { value: filterDrawer, setValue: setFilterDrawer, options: ["전체", "커미션", "두조", "우진", "합작", "기타"] },
        { value: filterSouta, setValue: setFilterSouta, options: ["전체", "오리진소우타", "2P소우타", "뇨우타", "2P뇨우타"] },
        { value: filterHoshi, setValue: setFilterHoshi, options: ["전체", "오리진호시", "2P호시", "뇨시", "2P뇨시"] },
    ];

    const uploadFilters = [
        { value: drawer, setValue: setDrawer, options: ["커미션", "두조", "우진", "합작", "기타"] },
        { value: souta, setValue: setSouta, options: ["오리진소우타", "2P소우타", "뇨우타", "2P뇨우타"] },
        { value: hoshi, setValue: setHoshi, options: ["오리진호시", "2P호시", "뇨시", "2P뇨시"] },
    ];

    return (
        <div className="flex flex-col items-center w-full h-full">
            <div className="h-[20%] z-[50]">
                <div className="text-white text-6xl font-mochiy mt-[24%]">Gallery</div>
            </div>

            <Window className="w-full h-full shadow-sm relative" content={
                <div className="h-full flex-col justify-center relative w-full max-w-7xl">
                    <div className="flex justify-center items-center pb-2 mt-2 gap-3 w-full z-50">
                        {filters.map((filter, idx) => (
                            <select
                                key={idx}
                                className="p-1 w-[15%] rounded-full bg-blue-200 text-blue-800 text-sm text-center transition appearance-none shadow-sm"
                                value={filter.value}
                                onChange={(e) => filter.setValue(e.target.value)}
                            >
                                {filter.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        ))}
                    </div>

                    <div className="h-[60vh] overflow-scroll overflow-x-hidden relative">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 p-6 pt-0 w-full max-w-6xl">
                            {filteredImages.map((img) => (
                                <div
                                    key={img.id}
                                    className="rounded-lg overflow-hidden border border-gray-300 cursor-pointer hover:scale-105 transition"
                                    onClick={() => setSelectedImage(img)}
                                >
                                    <img
                                        src={img.imageUrl}
                                        alt="갤러리 이미지"
                                        className="w-full object-cover aspect-square"
                                    />
                                </div>
                            ))}
                        </div>
                        <div ref={ref} className="h-4" />
                    </div>

                    {selectedImage && (
                        <div
                            className="fixed z-[80] top-0 left-0 w-full h-full flex justify-center items-center"
                            style={{ pointerEvents: deleteLoading ? "none" : "auto" }}
                        >
                            <Window
                                className="shadow-lg"
                                text={selectedImage.fileName.split("_").slice(1).join("_")}
                                onClose={() => setSelectedImage(null)}
                                content={
                                    <div
                                        className="relative mt-5 shadow-lg"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ pointerEvents: "auto" }}
                                    >
     
                                        <img
                                            src={selectedImage.imageUrl}
                                            alt="선택된 이미지"
                                            className="max-w-[90vw] max-h-[90vh]"
                                        />
                                        <button
                                            className="absolute bottom-2 right-2 bg-red-500 hover:bg-red-600 p-2 text-white rounded-lg flex items-center justify-center text-sm shadow-md"
                                            onClick={() => {
                                                setPendingDelete(selectedImage);
                                                openPasswordModal("delete");
                                            }}
                                            disabled={deleteLoading}
                                        >
                                            DELETE
                                        </button>

                                        {deleteLoading && (
                                            <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center z-20">
                                                <Loading />
                                            </div>
                                        )}
                                    </div>
                                }
                            />
                        </div>
                    )}


                    {showUpload && (
                        <div className="absolute top-1/2 left-1/2 z-50 transform -translate-x-1/2 -translate-y-1/2 w-[320px] shadow-lg">
                            <Window
                                onClose={onClose}
                                content={
                                    <div className="flex flex-col gap-4 items-center p-6 pt-9 rounded-lg shadow-lg w-[320px] relative">
                                        <button
                                            className="absolute top-0 right-3 w-3 h-3"
                                            onClick={onClose}
                                            disabled={uploadLoading}
                                            style={{ cursor: uploadLoading ? "not-allowed" : "pointer" }}
                                        />
                                        {imagePreview && (
                                            <img
                                                src={imagePreview}
                                                alt="미리보기"
                                                className="w-auto h-full aspect-square rounded-lg mt-2 object-cover border border-gray-300"
                                                style={{ filter: uploadLoading ? "blur(2px)" : "none", pointerEvents: uploadLoading ? "none" : "auto" }}
                                            />
                                        )}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-400 file:text-white hover:file:bg-blue-500 cursor-pointer"
                                            onChange={onFileChange}
                                            disabled={uploadLoading}
                                        />
                                        <div className="flex gap-2 w-full justify-center">
                                            {uploadFilters.map((filter, i) => (
                                                <select
                                                    key={i}
                                                    className="p-1 rounded-full bg-blue-400 text-white text-xs w-1/3 hover:bg-blue-500 transition text-center appearance-none"
                                                    value={filter.value}
                                                    onChange={(e) => filter.setValue(e.target.value)}
                                                    disabled={uploadLoading}
                                                >
                                                    {filter.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                </select>
                                            ))}
                                        </div>
                                        <button
                                            className="bg-blue-400 hover:bg-blue-500 text-white py-2 px-6 rounded font-semibold transition mt-2"
                                            onClick={handleUpload}
                                            disabled={uploadLoading}
                                        >
                                            업로드하기
                                        </button>

                                        {uploadLoading && (
                                            <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center z-20">
                                                <Loading />
                                            </div>
                                        )}
                                    </div>
                                }
                            />
                        </div>
                    )}



                    {passwordMode && (
                        <div className="absolute top-1/2 left-1/2 z-[100] transform -translate-x-1/2 -translate-y-1/2 w-[320px] shadow-lg">
                            <Window onClose={closePasswordModal} content={<Password onClose={closePasswordModal} onSuccess={handlePasswordSuccess} />} />
                        </div>
                    )}

                    <button
                        className="absolute bottom-4 right-12 text-white bg-blue-400 rounded-full w-12 h-12 text-4xl font-bold hover:bg-blue-500 transition z-50"
                        onClick={() => openPasswordModal("upload")}
                        aria-label="Open password modal"
                    >
                        +
                    </button>
                </div>
            } />

            <div className='w-full flex justify-center'>
                <img src="/assets/logo2.png" alt="로고 이미지" className="h-[10vh] object-cover" />
            </div>
        </div>
    );
};

export default Gallery;
